import { useState, useEffect } from 'react'
import {
  Typography,
  Box,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  LinearProgress,
  Tabs,
  Tab,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material'
import {
  Search as SearchIcon,
  Visibility as ViewIcon,
  Assessment as AssessmentIcon,
  Warning as WarningIcon,
  Error as ErrorIcon
} from '@mui/icons-material'
//import { getCustomerPolicies, getAsBuiltPolicyJson, getSubscriptionInfo, getDeploymentHistory, getAIUsage } from '../services/api'
import { getCustomerPolicies, getAsBuiltPolicyJson, getSubscriptionInfo, getDeploymentHistory} from '../services/api'
//import type { PolicyMetadata, SubscriptionInfo, Deployment, AIUsage } from '../services/api'
import type { PolicyMetadata, SubscriptionInfo, Deployment } from '../services/api'
import GapAssessment from './GapAssessment'

interface PoliciesViewProps {
  tenantId?: string;
}

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`policy-tabpanel-${index}`}
      aria-labelledby={`policy-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  )
}

export default function PoliciesView({ tenantId }: PoliciesViewProps) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [policies, setPolicies] = useState<PolicyMetadata[]>([])
  const [filteredPolicies, setFilteredPolicies] = useState<PolicyMetadata[]>([])
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null)
  const [deployments, setDeployments] = useState<Deployment[]>([])
  //const [aiUsage, setAiUsage] = useState<AIUsage | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [platformFilter, setPlatformFilter] = useState<string>('All')
  const [typeFilter, setTypeFilter] = useState<string>('All')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [selectedPolicy, setSelectedPolicy] = useState<PolicyMetadata | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [tabValue, setTabValue] = useState(0)
  const [mainTabValue, setMainTabValue] = useState(0)
  const [sortField, setSortField] = useState<string | null>(null)
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')
  const [skippedDialogOpen, setSkippedDialogOpen] = useState(false)
  const [failedDialogOpen, setFailedDialogOpen] = useState(false)
  const [asBuiltJson, setAsBuiltJson] = useState<string | null>(null)
  const [asBuiltLoading, setAsBuiltLoading] = useState(false)
  const [asBuiltError, setAsBuiltError] = useState<string | null>(null)

  useEffect(() => {
    if (tenantId) {
      loadPolicies()
    }
  }, [tenantId])

  useEffect(() => {
    filterPolicies()
  }, [policies, searchTerm, platformFilter, typeFilter, sortField, sortDirection])

  const loadPolicies = async () => {
    if (!tenantId) return
    
    try {
      setLoading(true)
      setError(null)
      //const [policyData, subInfo, deployHistory, aiStats] = await Promise.all([
      const [policyData, subInfo, deployHistory] = await Promise.all([
        getCustomerPolicies(tenantId),
        getSubscriptionInfo(tenantId),
        getDeploymentHistory(tenantId)
        //getAIUsage(tenantId).catch(() => null)
      ])

      setPolicies(policyData)
      setSubscription(subInfo)
      setDeployments(deployHistory)
      //setAiUsage(aiStats)
    } catch (err: any) {
      console.error('Error loading policies:', err)
      setError(err.message || 'Failed to load policies')
    } finally {
      setLoading(false)
    }
  }

  const filterPolicies = () => {
    let filtered = [...policies]

    // Helper function to parse DeviceTypes JSON string to array
    const getDeviceTypesArray = (policy: PolicyMetadata): string[] => {
      if (policy.DeviceTypes) {
        try {
          const parsed = JSON.parse(policy.DeviceTypes)
          return Array.isArray(parsed) ? parsed : []
        } catch {
          return []
        }
      }
      // Fallback to Platform for backward compatibility
      return policy.Platform ? [policy.Platform] : []
    }

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (p) =>
          p.PolicyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          p.PolicyType.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    // Platform/Device Type filter
    if (platformFilter !== 'All') {
      filtered = filtered.filter((p) => {
        const deviceTypes = getDeviceTypesArray(p)
        return deviceTypes.includes(platformFilter)
      })
    }

    // Type filter
    if (typeFilter !== 'All') {
      filtered = filtered.filter((p) => p.PolicyType === typeFilter)
    }

    if (sortField === 'Platform') {
      filtered = filtered.sort((a, b) => {
        const left = a.Platform || ''
        const right = b.Platform || ''
        const comparison = left.localeCompare(right)
        return sortDirection === 'asc' ? comparison : -comparison
      })
    }

    setFilteredPolicies(filtered)
    setPage(0) // Reset to first page when filters change
  }

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleViewPolicy = (policy: PolicyMetadata) => {
    setSelectedPolicy(policy)
    setDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
    setSelectedPolicy(null)
  }

  const handlePlatformTileClick = (platform: string) => {
    setMainTabValue(0)
    setPlatformFilter(platform)
    setTypeFilter('All')
    setSortField('Platform')
    setSortDirection('asc')
    setPage(0)
  }

  // Load as-built JSON when Technical Info tab is opened
  useEffect(() => {
    const loadAsBuilt = async () => {
      if (!tenantId || !selectedPolicy || tabValue !== 1) return
      if (!selectedPolicy.DeploymentID || !selectedPolicy.PolicyName) return
      try {
        setAsBuiltLoading(true)
        setAsBuiltError(null)
        setAsBuiltJson(null)
        const data = await getAsBuiltPolicyJson(tenantId, selectedPolicy.DeploymentID, selectedPolicy.PolicyName)
        if (typeof data === 'string') {
          setAsBuiltJson(data)
        } else {
          setAsBuiltJson(JSON.stringify(data, null, 2))
        }
      } catch (err: any) {
        setAsBuiltError(err.message || 'Failed to load as-built JSON')
      } finally {
        setAsBuiltLoading(false)
      }
    }
    loadAsBuilt()
  }, [tenantId, selectedPolicy, tabValue])

  const getPlatformChip = (policy: PolicyMetadata) => {
    // Parse DeviceTypes if available
    let deviceTypes: string[] = []
    if (policy.DeviceTypes) {
      try {
        const parsed = JSON.parse(policy.DeviceTypes)
        deviceTypes = Array.isArray(parsed) ? parsed : []
      } catch {
        // Fallback to Platform
        deviceTypes = policy.Platform ? [policy.Platform] : []
      }
    } else {
      // Fallback to Platform for backward compatibility
      deviceTypes = policy.Platform ? [policy.Platform] : []
    }

    const colors: Record<string, any> = {
      Windows: 'primary',
      macOS: 'secondary',
      Android: 'success',
      iPhone: 'error',
      iPad: 'info',
      Tenant: 'default',
      Base: 'default'
    }

    // If multiple device types, show them all as chips
    if (deviceTypes.length > 1) {
      return (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {deviceTypes.map((dt) => (
            <Chip key={dt} label={dt} color={colors[dt] || 'default'} size="small" variant="outlined" />
          ))}
        </Box>
      )
    }

    // Single device type
    const platform = deviceTypes[0] || 'Unknown'
    return <Chip label={platform} color={colors[platform] || 'default'} size="small" variant="outlined" />
  }

  const getSecurityLevelChip = (level: string) => {
    const colors: Record<string, any> = {
      Critical: 'error',
      High: 'warning',
      Medium: 'info',
      Low: 'success'
    }
    return <Chip label={level} color={colors[level] || 'default'} size="small" />
  }

  // Get unique platforms and types for filters
  const visiblePolicies = policies.filter(
    (p) => p.DeploymentStatus && ['deployed', 'modified', 'failed'].includes(p.DeploymentStatus.toLowerCase())
  )

  const skippedPolicies = policies.filter(
    (p) => (p.DeploymentStatus && p.DeploymentStatus.toLowerCase() === 'skipped') || p.SkipReason
  )
  const failedPolicies = policies.filter(
    (p) => p.DeploymentStatus && p.DeploymentStatus.toLowerCase() === 'failed'
  )
  // Build filter options from DeviceTypes (fallback to Platform if needed)
  const buildDeviceTypeOptions = (): string[] => {
    const set = new Set<string>()
    for (const p of policies) {
      if (p.DeviceTypes) {
        try {
          const parsed = JSON.parse(p.DeviceTypes)
          if (Array.isArray(parsed)) {
            parsed.forEach((dt: string) => dt && set.add(dt))
          }
        } catch {
          // ignore parse errors; fall back below
        }
      } else if (p.Platform) {
        set.add(p.Platform)
      }
    }
    return Array.from(set)
  }

  // Ensure standard device types are always available to avoid MUI out-of-range warnings
  const knownDeviceTypes = ['Windows', 'macOS', 'Android', 'iPhone', 'iPad', 'Tenant']
  const platforms = ['All', ...Array.from(new Set([...knownDeviceTypes, ...buildDeviceTypeOptions()]))]
  const policyTypes = ['All', ...new Set(policies.map((p) => p.PolicyType))]

  // Helper to check if policy has specific device type
  const hasDeviceType = (policy: PolicyMetadata, deviceType: string): boolean => {
    if (policy.DeviceTypes) {
      try {
        const parsed = JSON.parse(policy.DeviceTypes)
        return Array.isArray(parsed) && parsed.includes(deviceType)
      } catch {
        return false
      }
    }
    // Fallback to Platform for backward compatibility
    return policy.Platform === deviceType
  }

  // Calculate success rate from deployment history
  const totalDeployed = deployments.reduce((sum, d) => sum + d.successfulPolicies, 0)
  const totalAttempted = deployments.reduce((sum, d) => sum + d.totalPolicies, 0)
  const successRate = totalAttempted > 0 ? (totalDeployed / totalAttempted) * 100 : 0
  

  // Calculate statistics
  const stats = {
    total: visiblePolicies.length,
    deployed: visiblePolicies.filter((p) => p.Platform).length,
    windows: visiblePolicies.filter((p) => hasDeviceType(p, 'Windows')).length,
    macOS: visiblePolicies.filter((p) => hasDeviceType(p, 'macOS')).length,
    android: visiblePolicies.filter((p) => hasDeviceType(p, 'Android')).length,
    iPhone: visiblePolicies.filter((p) => hasDeviceType(p, 'iPhone')).length,
    iPad: visiblePolicies.filter((p) => hasDeviceType(p, 'iPad')).length,
    tenant: visiblePolicies.filter((p) => hasDeviceType(p, 'Tenant')).length,
    skipped: skippedPolicies.length,
    failed: visiblePolicies.filter((p) => p.DeploymentStatus?.toLowerCase() === 'failed').length
  }

  if (!tenantId) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          My Policies
        </Typography>
        <Alert severity="info" sx={{ mt: 2 }}>
          Loading customer information...
        </Alert>
      </Box>
    )
  }

  if (loading) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          My Policies
        </Typography>
        <LinearProgress sx={{ mt: 2 }} />
        <Typography variant="body2" sx={{ mt: 2, textAlign: 'center' }}>
          Loading policies...
        </Typography>
      </Box>
    )
  }

  if (error) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          My Policies
        </Typography>
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      </Box>
    )
  }

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          System Dashboard
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Management and monitoring for {subscription?.CompanyName || 'your tenant'}
        </Typography>
      </Box>

      {/* High Level Metrics Row */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Paper elevation={1} sx={{ p: 2, textAlign: 'center', borderTop: 4, borderColor: 'success.main', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Typography variant="h4" color="success.main">
              {successRate.toFixed(1)}%
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Deployment Success Rate
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper elevation={1} sx={{ p: 2, textAlign: 'center', borderTop: 4, borderColor: 'primary.main', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Typography variant="h4">{subscription?.TotalDeployments ?? 0}</Typography>
            <Typography variant="body2" color="text.secondary">
              Total Cycles Run
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper elevation={1} sx={{ p: 2, textAlign: 'center', borderTop: 4, borderColor: subscription?.SubscriptionStatus === 'Active' ? 'success.main' : 'warning.main', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Chip label={subscription?.SubscriptionStatus || 'Unknown'} color={subscription?.SubscriptionStatus === 'Active' ? 'success' : 'warning'} sx={{ mb: 1, fontWeight: 'bold' }} />
            <Typography variant="body2" color="text.secondary">
              Entitlement: {subscription?.EntitlementType || 'Base'}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Main Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={mainTabValue} onChange={(_e, newValue) => setMainTabValue(newValue)}>
          <Tab label="Deployed Policies" />
          <Tab 
            label="Gap Assessment" 
            icon={<AssessmentIcon />} 
            iconPosition="start"
          />
        </Tabs>
      </Box>

      {/* Deployed Policies Tab */}
      <TabPanel value={mainTabValue} index={0}>
        {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={4} md={2}>
          <Paper elevation={2} sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4">{stats.total}</Typography>
            <Typography variant="body2" color="text.secondary">
              Total
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Paper
            elevation={2}
            role="button"
            onClick={() => handlePlatformTileClick('Windows')}
            sx={{
              p: 2,
              textAlign: 'center',
              cursor: 'pointer',
              opacity: 1
            }}
          >
            <Typography variant="h4" color="primary">
              {stats.windows}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Windows
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Paper
            elevation={2}
            role="button"
            onClick={() => handlePlatformTileClick('macOS')}
            sx={{
              p: 2,
              textAlign: 'center',
              cursor: 'pointer',
              opacity: 1
            }}
          >
            <Typography variant="h4" color="secondary">
              {stats.macOS}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              macOS
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Paper
            elevation={2}
            role="button"
            onClick={() => handlePlatformTileClick('Android')}
            sx={{
              p: 2,
              textAlign: 'center',
              cursor: 'pointer',
              opacity: 1
            }}
          >
            <Typography variant="h4" color="success.main">
              {stats.android}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Android
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Paper
            elevation={2}
            role="button"
            onClick={() => handlePlatformTileClick('iPad')}
            sx={{
              p: 2,
              textAlign: 'center',
              cursor: 'pointer',
              opacity: 1
            }}
          >
            <Typography variant="h4" color="info.main">
              {stats.iPad}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              iPad
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Paper
            elevation={2}
            role="button"
            onClick={() => handlePlatformTileClick('iPhone')}
            sx={{
              p: 2,
              textAlign: 'center',
              cursor: 'pointer',
              opacity: 1
            }}
          >
            <Typography variant="h4" color="info.main">
              {stats.iPhone}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              iPhone
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Paper
            elevation={2}
            role="button"
            onClick={() => handlePlatformTileClick('Tenant')}
            sx={{
              p: 2,
              textAlign: 'center',
              cursor: 'pointer',
              opacity: 1
            }}
          >
            <Typography variant="h4" color="text.secondary">
              {stats.tenant}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Tenant
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Paper
            elevation={2}
            role="button"
            onClick={() => stats.skipped > 0 && setSkippedDialogOpen(true)}
            sx={{
              p: 2,
              textAlign: 'center',
              cursor: 'pointer',
              opacity: 1
            }}
          >
            <Typography variant="h4" color="warning.main">
              {stats.skipped}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Skipped
            </Typography>
          </Paper>
        </Grid>
        {stats.failed > 0 && (
          <Grid item xs={6} sm={4} md={2}>
            <Paper
              elevation={2}
              role="button"
              onClick={() => setFailedDialogOpen(true)}
              sx={{
                p: 2,
                textAlign: 'center',
                cursor: 'pointer',
                opacity: 1
              }}
            >
              <Typography variant="h4" color="error.main">
                {stats.failed}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Failed
              </Typography>
            </Paper>
          </Grid>
        )}
      </Grid>

      <Card>
        <CardContent>
          {/* Filters */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search policies..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Platform</InputLabel>
                <Select
                  value={platformFilter}
                  label="Platform"
                  onChange={(e) => setPlatformFilter(e.target.value)}
                >
                  {platforms.map((platform) => (
                    <MenuItem key={platform} value={platform}>
                      {platform}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select value={typeFilter} label="Type" onChange={(e) => setTypeFilter(e.target.value)}>
                  {policyTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Results Summary */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Showing {filteredPolicies.length} of {policies.length} policies
            </Typography>
          </Box>

          {/* Policies Table */}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Policy Name</TableCell>
                  <TableCell>Platform</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Security Level</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredPolicies.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        No deployed policies found
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {policies.length === 0 
                          ? "You haven't deployed any policies yet. Visit the Deploy Config page to get started."
                          : "No policies match your current filters. Try adjusting your search criteria."}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredPolicies
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((policy) => (
                      <TableRow key={policy.CustomerPolicyID} hover>
                        <TableCell>
                          <Typography variant="body2">{policy.PolicyName}</Typography>
                        </TableCell>
                        <TableCell>{getPlatformChip(policy)}</TableCell>
                        <TableCell>
                          <Typography variant="body2">{policy.PolicyType}</Typography>
                        </TableCell>
                        <TableCell>{getSecurityLevelChip(policy.SecurityLevel)}</TableCell>
                        <TableCell align="center">
                          <IconButton size="small" onClick={() => handleViewPolicy(policy)} color="primary">
                            <ViewIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={filteredPolicies.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </CardContent>
      </Card>
      </TabPanel>

      {/* Gap Assessment Tab */}
      <TabPanel value={mainTabValue} index={1}>
        {tenantId && <GapAssessment tenantId={tenantId} />}
      </TabPanel>

      {/* Skipped Policies Dialog */}
      <Dialog open={skippedDialogOpen} onClose={() => setSkippedDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Skipped Policies</DialogTitle>
        <DialogContent dividers>
          {skippedPolicies.length === 0 ? (
            <Alert severity="info" sx={{ mt: 1 }}>
              No skipped policies recorded for this tenant.
            </Alert>
          ) : (
            <List dense>
              {skippedPolicies.map((policy) => (
                <ListItem key={policy.CustomerPolicyID} alignItems="flex-start">
                  <ListItemIcon>
                    <WarningIcon color="warning" />
                  </ListItemIcon>
                  <ListItemText
                    primary={policy.PolicyName}
                    secondary={
                      <Typography variant="body2" color="text.secondary">
                        {policy.SkipReason || policy.DeploymentStatus || 'Skipped during deployment'}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSkippedDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Policy Details Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h6">{selectedPolicy?.PolicyName}</Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            {selectedPolicy && (
              <>
                {getPlatformChip(selectedPolicy)}
                {getSecurityLevelChip(selectedPolicy.SecurityLevel)}
              </>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedPolicy && (
            <>
              <Tabs value={tabValue} onChange={(_e, newValue) => setTabValue(newValue)}>
                <Tab label="Details" />
                <Tab label="Technical Info" />
              </Tabs>

              <TabPanel value={tabValue} index={0}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Policy Type
                    </Typography>
                    <Typography variant="body1">{selectedPolicy.PolicyType}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Platform
                    </Typography>
                    <Typography variant="body1">{selectedPolicy.Platform}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Security Level
                    </Typography>
                    <Typography variant="body1">{selectedPolicy.SecurityLevel}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Ownership Type
                    </Typography>
                    <Typography variant="body1">{selectedPolicy.OwnershipType}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      License Requirement
                    </Typography>
                    <Typography variant="body1">{selectedPolicy.LicenseRequirement}</Typography>
                  </Grid>
                  {selectedPolicy.RequiredServicePlans && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        Required Service Plans
                      </Typography>
                      <Typography variant="body1">{selectedPolicy.RequiredServicePlans}</Typography>
                    </Grid>
                  )}
                </Grid>

                <Divider sx={{ my: 2 }} />

                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Assignment
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  {selectedPolicy.AssignToAllUsers && (
                    <Chip label="Assign to All Users" size="small" color="primary" />
                  )}
                  {selectedPolicy.AssignToAllDevices && (
                    <Chip label="Assign to All Devices" size="small" color="secondary" />
                  )}
                </Box>
              </TabPanel>

              <TabPanel value={tabValue} index={1}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Policy ID
                    </Typography>
                    <Typography variant="body1" fontFamily="monospace">
                      {selectedPolicy.PolicyID}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Graph API Endpoint
                    </Typography>
                    <Typography variant="body2" fontFamily="monospace" sx={{ wordBreak: 'break-all' }}>
                      {selectedPolicy.GraphApiEndpoint}
                    </Typography>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  As-Built JSON
                </Typography>
                {asBuiltLoading && (
                  <Typography variant="body2" color="text.secondary">Loading as-built JSON...</Typography>
                )}
                {asBuiltError && (
                  <Alert severity="error" sx={{ mt: 1 }}>{asBuiltError}</Alert>
                )}
                {!asBuiltLoading && !asBuiltError && asBuiltJson && (
                  <Box
                    component="pre"
                    tabIndex={0}
                    onContextMenu={(e) => e.preventDefault()}
                    onCopy={(e) => e.preventDefault()}
                    onKeyDown={(e) => {
                      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'c') {
                        e.preventDefault()
                      }
                    }}
                    sx={{
                      fontSize: 12,
                      p: 2,
                      bgcolor: 'grey.100',
                      borderRadius: 1,
                      overflow: 'auto',
                      maxHeight: 400,
                      userSelect: 'none',
                      WebkitUserSelect: 'none',
                      MozUserSelect: 'none',
                      msUserSelect: 'none'
                    }}
                  >
                    {asBuiltJson}
                  </Box>
                )}
              </TabPanel>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Failed Policies Dialog */}
      <Dialog open={failedDialogOpen} onClose={() => setFailedDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Failed Policies</DialogTitle>
        <DialogContent dividers>
          {failedPolicies.length === 0 ? (
            <Alert severity="info" sx={{ mt: 1 }}>
              No failed policies recorded for this tenant.
            </Alert>
          ) : (
            <List dense>
              {failedPolicies.map((policy) => (
                <ListItem key={policy.CustomerPolicyID} alignItems="flex-start">
                  <ListItemIcon>
                    <ErrorIcon color="error" />
                  </ListItemIcon>
                  <ListItemText
                    primary={policy.PolicyName}
                    secondary={
                      <Typography variant="body2" color="text.secondary">
                        {policy.SkipReason || 'Failed during deployment'}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFailedDialogOpen(false)}>Close</Button>
          {failedPolicies.length > 0 && (
            <Button 
              variant="contained" 
              color="error"
              onClick={() => {
                // Find the most recent deployment ID from failed policies
                const recentDeploymentId = failedPolicies[0]?.DeploymentID
                if (recentDeploymentId) {
                  window.location.href = `/deploy?retryFailedDeploymentId=${recentDeploymentId}`
                }
              }}
            >
              Retry Failed
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  )
}
